/**
 * Setup-service call-back lead (issue #2269 — mobile parity for the web
 * dashboard's `SetupCallbackCard`).
 *
 * A builder leaves a phone number asking us to ring back and set their first
 * job up with them. The web card posts to `/api/leads/setup-callback`; the
 * native Home card posts to `/api/mobile/v1/leads/setup-callback`. Both routes
 * are thin wrappers over the ONE server chokepoint `lib/setup-service-callback.ts`.
 *
 * This module also owns the OFFER-ELIGIBILITY map, because eligibility is the
 * one rule both the client (which decides whether to render the card) and the
 * server (which re-checks before alerting) must agree on. Keeping it here —
 * the only place web AND React Native can both import from — is what stops the
 * surface and the route disagreeing the way they did in #2061.
 */
import { z } from "zod";
import { type PlanTier } from "../auth/plan-state";

/**
 * Which plans are OFFERED the £249 setup-service call-back.
 *
 * Deliberately a full map rather than `!isSiteControl(plan)`, with a
 * `satisfies Record<PlanTier, boolean>` so adding a plan tier is a COMPILE
 * ERROR until someone decides whether it gets the offer. A silent default is
 * how a paid-service prompt ends up in front of the wrong tier. (`lib/plan.ts`
 * carries a compile-time assertion that its `Plan` union and `PlanTier` are
 * the same set, so that guarantee still bites when a tier is added web-side.)
 *
 * Site Control is excluded because it already has its own guided onboarding
 * (`SiteControlHeroSignpost` / the walkthrough lead flow) — offering the
 * self-serve setup service there would double-sell the same thing.
 *
 * Originally starter-only, which meant the people best placed to test it —
 * and the CEO, on Pro — could never see it. It is a paid service, so every
 * plan that does not already get hands-on onboarding is a legitimate audience.
 */
const SETUP_SERVICE_OFFER_TIERS = {
  starter: true,
  pro_monthly: true,
  pro_annual: true,
  business: true,
  site_control: false,
} as const satisfies Record<PlanTier, boolean>;

/**
 * Accepts a raw `string` as well as a typed `PlanTier`, because the dashboard
 * and the leads route both hold `account.plan` as an untyped string (the same
 * reason `isBusinessPlan` exists in lib/account-context.ts). An unrecognised
 * value returns FALSE — a plan we cannot identify does not get shown a paid
 * service offer.
 */
export function isSetupServiceOfferable(plan: PlanTier | string): boolean {
  return SETUP_SERVICE_OFFER_TIERS[plan as PlanTier] ?? false;
}

/**
 * Upper bound on the typed phone string. Mirrors `MAX_PHONE_INPUT_LENGTH` in
 * `lib/uk-phone.ts` — contracts may not import from `lib/`, so the two are
 * pinned together by `__tests__/lib/leads-setup-callback.test.ts` instead.
 */
export const SETUP_CALLBACK_MAX_PHONE_LENGTH = 50;

/** POST body — the phone number IS the submission, so blank is invalid. */
export const SetupCallbackRequestSchema = z
  .object({
    phone: z.string().min(1).max(SETUP_CALLBACK_MAX_PHONE_LENGTH),
  })
  .strict();
export type SetupCallbackRequest = z.infer<typeof SetupCallbackRequestSchema>;

/**
 * Why nothing was sent.
 *
 *   - `plan_not_offered` — the account's tier is not in the offer map (or the
 *     caller has no account at all). Nothing was sent and nothing is promised.
 *   - `deduped` — today's call-back for this user was ALREADY delivered. The
 *     promise stands, so this is a success for the caller.
 */
export const SetupCallbackSkipReasonSchema = z.enum([
  "plan_not_offered",
  "deduped",
]);
export type SetupCallbackSkipReason = z.infer<
  typeof SetupCallbackSkipReasonSchema
>;

/**
 * POST response.
 *
 * `ok` answers the only question the card actually has — "is a call-back
 * promised?" — so `deduped` comes back as `ok: true` with the reason attached,
 * and `plan_not_offered` as `ok: false`. The web card re-derives exactly this
 * from its two-shape JSON; making it explicit on the wire means the native
 * card does not have to re-derive it a second, subtly different way.
 */
export const SetupCallbackResponseSchema = z.object({
  ok: z.boolean(),
  skipped: SetupCallbackSkipReasonSchema.nullable(),
});
export type SetupCallbackResponse = z.infer<typeof SetupCallbackResponseSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base.
export const SetupCallbackResponseStrict = SetupCallbackResponseSchema.strict();
